import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import "../../../i18/config";
import { getChannelApproval, saveChannelApproval } from "../../../api";
import { ChannelApprovalPolicy } from "../types";
import InputSwitch from "../../../components/InputSwitch";
import OptionSelect from "../ui/OptionSelect";
import AssigneePicker from "./AssigneePicker";
import { emptyAssignees, useDirectory } from "./useDirectory";

const EDITABLE = ['text', 'title', 'activeFrom'];

const defaults = (): ChannelApprovalPolicy => ({
    assignees: emptyAssignees(),
    policy: 'any',
    editableFields: [],
    timeoutHours: 0,
    onTimeout: 'reject',
    escalateTo: null,
    requireRejectComment: false,
    adminBypass: false,
});

/**
 * The channel's approval setting, in the channel panel. Saved on its own
 * (admins only), not with the channel form.
 */
const ChannelApprovalSettings = ({ channelUuid }: { channelUuid: string }) => {
    const { t } = useTranslation(['workflow']);
    const { users, groups } = useDirectory();
    const [required, setRequired] = useState(false);
    const [policy, setPolicy] = useState<ChannelApprovalPolicy>(defaults());
    const [canEdit, setCanEdit] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setLoaded(false);
        getChannelApproval(channelUuid).then((r: any) => {
            setRequired(!!r.item.approvalRequired);
            setPolicy({ ...defaults(), ...(r.item.approvalPolicy || {}), assignees: { ...emptyAssignees(), ...(r.item.approvalPolicy?.assignees || {}) } });
            setCanEdit(!!r.item.canEdit);
            setLoaded(true);
        }).catch(() => setLoaded(false));
    }, [channelUuid]);

    if (!loaded) {
        return null;
    }

    const set = (patch: Partial<ChannelApprovalPolicy>) => setPolicy({ ...policy, ...patch });
    const save = () => {
        setSaving(true);
        saveChannelApproval(channelUuid, { approvalRequired: required, approvalPolicy: policy })
            .then(() => toast.success(t('workflow:saved')))
            .catch((e: any) => toast.error(e?.message || t('workflow:actionFailed')))
            .finally(() => setSaving(false));
    };
    const id = 'ch-approval-' + channelUuid;

    return (
        <div className="border rounded p-3 mb-3">
            <InputSwitch id={id} className="form-switch-md" placeholder={t('workflow:channelApprovalRequired')}
                value={required} disabled={!canEdit} onChange={() => setRequired(!required)} />
            <div className="form-text mb-3">{t('workflow:channelApprovalHint')}</div>

            {required && <>
                <div className="mb-3">
                    <label className="form-label">{t('workflow:approvers')}</label>
                    <AssigneePicker id={id + '-a'} value={policy.assignees} onChange={(assignees) => set({ assignees })} users={users} groups={groups} disabled={!canEdit} />
                    <div className="form-text">{t('workflow:approversHint')}</div>
                </div>

                <div className="mb-3">
                    <label className="form-label">{t('workflow:whoMustApprove')}</label>
                    <OptionSelect required readOnly={!canEdit} value={policy.policy} onChange={(v) => v && set({ policy: v })}
                        options={[{ value: 'any', label: t('workflow:policyAny') }, { value: 'all', label: t('workflow:policyAll') }]} />
                </div>
                <div className="mb-3">
                    <label className="form-label" htmlFor={id + '-timeout'}>{t('workflow:timeLimitHours')}</label>
                    <input id={id + '-timeout'} type="number" min={0} className="form-control" value={policy.timeoutHours} disabled={!canEdit}
                        onChange={(e) => set({ timeoutHours: Math.max(0, parseInt(e.target.value, 10) || 0) })} />
                </div>
                <div className="mb-3">
                    <label className="form-label">{t('workflow:whenTimeIsUp')}</label>
                    <OptionSelect required readOnly={!canEdit || policy.timeoutHours === 0} value={policy.onTimeout} onChange={(v) => v && set({ onTimeout: v })}
                        options={[
                            { value: 'reject', label: t('workflow:onTimeout_reject') },
                            { value: 'approve', label: t('workflow:onTimeout_approve') },
                            { value: 'escalate', label: t('workflow:onTimeout_escalate') },
                        ]} />
                </div>

                {policy.onTimeout === 'escalate' && policy.timeoutHours > 0 &&
                    <div className="mb-3">
                        <label className="form-label">{t('workflow:escalateTo')}</label>
                        <AssigneePicker id={id + '-e'} value={{ ...emptyAssignees(), ...(policy.escalateTo || {}) }} onChange={(escalateTo) => set({ escalateTo })} users={users} groups={groups} disabled={!canEdit} />
                    </div>
                }

                <label className="form-label">{t('workflow:approversMayEdit')}</label>
                <div className="d-flex flex-wrap gap-3 mb-3">
                    {EDITABLE.map((f) => (
                        <div key={f} className="form-check">
                            <input id={id + f} className="form-check-input" type="checkbox" disabled={!canEdit} checked={policy.editableFields.includes(f)}
                                onChange={() => set({ editableFields: policy.editableFields.includes(f) ? policy.editableFields.filter((v) => v !== f) : [...policy.editableFields, f] })} />
                            <label className="form-check-label" htmlFor={id + f}>{t('workflow:editable_' + f)}</label>
                        </div>
                    ))}
                </div>
                <div className="form-check mb-2">
                    <input id={id + 'rc'} className="form-check-input" type="checkbox" disabled={!canEdit} checked={policy.requireRejectComment} onChange={(e) => set({ requireRejectComment: e.target.checked })} />
                    <label className="form-check-label" htmlFor={id + 'rc'}>{t('workflow:rejectionNeedsComment')}</label>
                </div>
                <div className="form-check mb-3">
                    <input id={id + 'ab'} className="form-check-input" type="checkbox" disabled={!canEdit} checked={policy.adminBypass} onChange={(e) => set({ adminBypass: e.target.checked })} />
                    <label className="form-check-label" htmlFor={id + 'ab'}>{t('workflow:adminBypass')}</label>
                </div>
            </>}

            {canEdit
                ? <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>{t('workflow:saveApprovalSetting')}</button>
                : <div className="text-muted fs-13">{t('workflow:onlyAdminsChangeApproval')}</div>}
        </div>
    );
};

export default ChannelApprovalSettings;
